import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import { withStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import { Typography as Text } from '@mui/material';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import * as selectors from '../selectors';
import actions from '../actions';
import { validateEmail } from '../helper/Validations';

const styles = (theme) => ({
  formRoot: {
    maxWidth: 584,
    margin: '0 auto',
    padding: 0,
  },
  questionWrap: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 7,
    padding: 0,
  },
  questionHeader: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.2,
  },
  asterisk: {
    color: 'red',
    marginLeft: 2,
    fontWeight: 300,
    fontSize: '1rem',
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
  },
  textField: {
    minWidth: 150,
    marginRight: 12,
  },
  ageField: {
    width: 80,
    marginRight: 8,
    textAlign: 'center',
  },
  genderField: {
    width: 120,
    marginRight: 8,
  },
  emailBox: {
    flex: 1,
    minWidth: 0,
    marginLeft: 8,
  },
  helperText: {
    fontSize: '0.85rem',
    color: '#888',
    margin: '2px 0 0 0',
  },
  actionsWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 16,
  },
  loginLink: {
    color: 'red',
    fontSize: '0.75rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: 0.03333,
  },
  requiredFields: {
    color: 'red',
    fontSize: '0.9rem',
    margin: '8px 0 0 0',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: 8,
  },
  termsAndConditionsLink: {
    alignItems: 'start',
  },
  termsAndConditionsLinkCheck: {
    paddingRight: '5px',
    paddingTop: '1px',
  },
  blackCircle: {
    width: '5px',
    height: '5px',
    backgroundColor: '#000000',
  },
  actionBtn: {
    marginLeft: '10px',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: 'white',
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '0px',
    },
  },
  logoContainer: {
    flexGrow: 1,
    resizeMode: 'contain',
  },
  headerBar: {
    backgroundColor: '#fff', // Hardcoding the header to white as agreed. Most logos will have to be placed in a white bg anyway.
  },
});

function RegisterForm({
  liveProfile,
  details,
  classes,
  updateDetail,
  registerClient,
  showPasswordless,
  termsAndConditionsTicked,
  handleTermsAndConditionsTicked,
  accountDetails,
  extensionData,
  registration,
}) {
  const companyTAndC =
    accountDetails &&
    accountDetails.extensionData &&
    accountDetails.extensionData.extensionData &&
    accountDetails.extensionData.extensionData.DnsInfo &&
    accountDetails.extensionData.extensionData.DnsInfo.companyTermsConditions;

  const { termsAndConditionsText, termsAndConditionsLink } = registration;
  const { name: companyName } = accountDetails;
  const makeTCsLink = () =>
    parse(termsAndConditionsText, {
      replace() {
        const termsAndConditions = termsAndConditionsText.replace(
          'company_name',
          (accountDetails &&
            accountDetails.extensionData &&
            accountDetails.extensionData.extensionData &&
            accountDetails.extensionData.extensionData.StaticContent &&
            accountDetails.extensionData.extensionData.StaticContent.companyname) ||
            companyName,
        );
        const preTerms = termsAndConditions.split(termsAndConditionsLink)[0];
        const postTerms = termsAndConditions.split(termsAndConditionsLink)[1];

        return (
          (
            <>
              <span style={{ fontSize: '0.85rem' }}>{preTerms}</span>

              <a href={companyTAndC} target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>

              {postTerms}
            </>
          ) || (
            <>
              {preTerms}
              <Link to="/t&c" style={{ whiteSpace: 'nowrap' }}>
                {termsAndConditionsLink}
              </Link>
              {postTerms}
            </>
          )
        );
      },
    });

  const canSubmit =
    liveProfile &&
    termsAndConditionsTicked &&
    details.name.first &&
    details.name.surname &&
    Number(details.age || 0) >= 18 &&
    validateEmail(details.email);

  return (
    <div className={classes.formRoot}>
      <div className={classes.questionWrap}>
        <div className={classes.questionHeader}>
          My name is<span className={classes.asterisk}>*</span>
        </div>
        <div className={classes.flexRow}>
          <TextField
            label="First Name *"
            className={classes.textField}
            value={details.name.first || ''}
            onChange={(e) => updateDetail('name.first', e.target.value)}
            variant="standard"
          />{' '}
          &nbsp;&nbsp;&nbsp;
          <TextField
            label="Middle Names"
            className={classes.textField}
            value={details.name.middle || ''}
            onChange={(e) => updateDetail('name.middle', e.target.value)}
            variant="standard"
          />{' '}
          &nbsp;&nbsp;&nbsp;
          <TextField
            label="Surname *"
            className={classes.textField}
            value={details.name.surname || ''}
            onChange={(e) => updateDetail('name.surname', e.target.value)}
            variant="standard"
          />
        </div>
      </div>

      <div className={classes.questionWrap}>
        <div className={classes.flexRow}>
          <span className={classes.questionHeader}>
            I am a<span className={classes.asterisk}>*</span>
          </span>
          <TextField
            className={classes.ageField}
            value={details.age || ''}
            onChange={(e) => updateDetail('age', e.target.value)}
            type="number"
            variant="standard"
          />
          <span className={classes.questionHeader}>&nbsp;year old&nbsp;&nbsp;&nbsp;</span>
          <Select
            value={details.gender || ''}
            onChange={(e) => updateDetail('gender', e.target.value)}
            className={classes.genderField}
            displayEmpty
            variant="standard"
          >
            <MenuItem value="Unspecified">Unspecified</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </div>
        <div>
          <FormHelperText>
            Our service is only available to those 18 years of age and older
          </FormHelperText>
        </div>
      </div>

      <div className={classes.questionWrap}>
        <div className={classes.flexRow}>
          <span className={classes.questionHeader}>and I am</span>
          <Select
            value={details.maritalStatus || ''}
            onChange={(e) => updateDetail('maritalStatus', e.target.value)}
            className={classes.emailBox}
            displayEmpty
            variant="standard"
            name="relationship"
            placeholder="Relationship"
            inputProps={{
              id: 'relationship-required',
            }}
          >
            <MenuItem value="" disabled>
              Relationship
            </MenuItem>
            <MenuItem selected value="unspecified">
              Unspecified
            </MenuItem>
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="widow">Widowed</MenuItem>
            <MenuItem value="civilpartnership">Civil Partnership</MenuItem>
            <MenuItem value="inpartnership">Living as Partners</MenuItem>
            <MenuItem value="separated">Separated</MenuItem>
          </Select>
        </div>
      </div>

      <div className={classes.questionWrap}>
        <div className={classes.flexRow}>
          <span className={classes.questionHeader}>
            and my email is&nbsp;&nbsp;<span className={classes.asterisk}>*</span>
          </span>
          <TextField
            placeholder="myemail@provider.com"
            value={details.email || ''}
            onChange={(e) => updateDetail('email', e.target.value)}
            className={classes.emailBox}
            variant="standard"
            fullWidth
          />
        </div>
        {details.email && !validateEmail(details.email) && (
          <div>
            <FormHelperText color="red">Please enter a valid email to continue</FormHelperText>
          </div>
        )}
      </div>

      <FormHelperText>
        <span className={classes.asterisk}>*</span>
        Required fields
      </FormHelperText>

      <div className={classes.checkboxRow}>
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAndConditionsTicked}
              onChange={handleTermsAndConditionsTicked}
              color="primary"
              className={classes.termsAndConditionsLinkCheck}
            />
          }
          label={makeTCsLink()}
          className={classes.termsAndConditionsLink}
        />
      </div>

      <div className={classes.actionsWrap}>
        <Button
          id="registerClient"
          variant="contained"
          color="primary"
          onClick={registerClient}
          disabled={!canSubmit}
          style={{ marginBottom: '8px' }}
        >
          GET STARTED
        </Button>
        {/* <div style={{cursor: 'pointer'}} className={classes.loginLink} onClick={showPasswordless}>
          OR LOG IN TO CONTINUE
        </div> */}

        <Button color="primary" className={classes.loginLink} onClick={showPasswordless}>
          <Text color="inherit" variant="caption">
            or Log in to continue
          </Text>
        </Button>
      </div>
    </div>
  );
}

export default connect(
  (state, _) => ({
    details: selectors.getDetails(state),
    termsAndConditionsTicked: selectors.getTermsAndConditionsTicked(state),
  }),
  (dispatch, ownProps) => ({
    updateDetail(path, value) {
      dispatch(actions.app.data.updateUserData(path, value));
    },
    registerClient() {
      if (ownProps.iframe) {
        dispatch(actions.app.temporary.openInNewWindow());
      } else {
        dispatch(actions.app.data.registerClient(ownProps.iframe));
      }
    },
    handleTermsAndConditionsTicked(event) {
      dispatch(actions.app.appState.setTermsAndConditionsTicked(event.target.checked));
    },
  }),
)(withStyles(styles)(RegisterForm));
